/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ApiService, ManagedUser } from '@midas/shared/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { map, Observable } from 'rxjs';
import { IQuerySingle } from '../ui-query-single';
import { PagedResult } from '../ui-query-select';

@Component({
  selector: 'midas-ui-user-query-single',
  templateUrl: './ui-user-query-single.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    TranslocoModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiUserQuerySingleComponent),
      multi: true,
    },
  ],
})
export class UiUserQuerySingleComponent extends IQuerySingle<ManagedUser> {
  @Input() includeAllOrgs = false;

  constructor(
    private api: ApiService,
    private transloco: TranslocoService,
    protected _change: ChangeDetectorRef,
  ) {
    super(_change);
  }

  getItemsBySearchString(
    searchString?: string | null,
    startingIndex = 0,
    numberOfRecords = 10,
  ): Observable<PagedResult<ManagedUser>> {
    return this.api
      .get<{ count: number; managedUsers: ManagedUser[] }>('/managed-user', {
        startingIndex,
        numberOfRecords,
        searchString,
        showCurrentUser: true,
        includeAllOrganizations: this.includeAllOrgs,
      })
      .pipe(map((res) => ({ count: res.count, items: res.managedUsers })));
  }

  getItemById(id: number): Observable<ManagedUser | null> {
    return this.api
      .get<{ count: number; managedUsers: ManagedUser[] }>('/managed-user', {
        startingIndex: 0,
        numberOfRecords: 1,
        ids: [id],
        includeAllOrganizations: this.includeAllOrgs,
      })
      .pipe(map((res) => (res.count > 0 ? res.managedUsers[0] : null)));
  }

  getDisplayName(item: ManagedUser): string {
    return item.displayName || '';
  }

  getPlaceholderText(): string {
    return this.transloco.translate('Search Users');
  }
}
